import React, { useEffect, useState, type FormEvent } from "react";
import { api, setTokenGetter } from "../api";
import { Alert, Spinner } from "../common/ui";
import { AdminDashboard } from "./AdminDashboard";
import { CaseList } from "./CaseList";
import { CaseDetail } from "./CaseDetail";

const TOKEN_KEY = "rz_staff_token";
const USER_KEY = "rz_staff_user";

type Page = { name: "dashboard" } | { name: "cases" } | { name: "case"; id: string };

export function AdminApp() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<{ fullName: string; role: string } | null>(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [page, setPage] = useState<Page>({ name: "dashboard" });

  // Set synchronously during render (not in useEffect): child components
  // (e.g. AdminDashboard) fire their own mount effects before a parent's
  // effect runs, so an effect-based getter here would still be stale
  // (pointing at the pre-login `null` token) for the very first fetch
  // right after login, causing a spurious 401 "missing bearer token".
  setTokenGetter(() => token);

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }

  if (!token) {
    return (
      <StaffLoginScreen
        onLoggedIn={(t, u) => {
          localStorage.setItem(TOKEN_KEY, t);
          localStorage.setItem(USER_KEY, JSON.stringify(u));
          setToken(t);
          setUser(u);
        }}
      />
    );
  }

  return (
    <div className="admin-shell">
      <div className="admin-sidebar">
        <div className="brand">Raymand <span className="accent">×</span> Zoodex</div>
        <div className="admin-nav">
          <button className={page.name === "dashboard" ? "active" : ""} onClick={() => setPage({ name: "dashboard" })}>داشبورد</button>
          <button className={page.name !== "dashboard" ? "active" : ""} onClick={() => setPage({ name: "cases" })}>پرونده‌ها</button>
        </div>
        <div style={{ position: "absolute", bottom: 20, insetInlineStart: 14, insetInlineEnd: 14 }}>
          <div className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{user?.fullName}</div>
          <div className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>{user?.role}</div>
          <button className="btn btn-ghost btn-sm mt-2" style={{ color: "#fff", padding: 0 }} onClick={logout}>خروج</button>
        </div>
      </div>
      <div className="admin-main">
        {page.name === "dashboard" && <AdminDashboard onOpenCases={() => setPage({ name: "cases" })} />}
        {page.name === "cases" && <CaseList onOpenCase={(id) => setPage({ name: "case", id })} />}
        {page.name === "case" && <CaseDetail caseId={page.id} onBack={() => setPage({ name: "cases" })} />}
      </div>
    </div>
  );
}

function StaffLoginScreen({ onLoggedIn }: { onLoggedIn: (token: string, user: { fullName: string; role: string }) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.staffLogin(email.trim(), password);
      onLoggedIn(res.token, { fullName: res.user.fullName, role: res.user.role });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="center-screen">
      <div className="card login-card">
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div className="card-title" style={{ fontSize: 18 }}>پنل مدیریت رایمند</div>
          <p>ورود کارشناسان رایمند، زودکس و بانک صادرات</p>
        </div>
        {error && <Alert kind="error">{error}</Alert>}
        <form onSubmit={submit}>
          <div className="field">
            <label>ایمیل</label>
            <input type="text" value={email} onChange={(e: any) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label>رمز عبور</label>
            <input type="password" value={password} onChange={(e: any) => setPassword(e.target.value)} required />
          </div>
          <button className="btn btn-primary" disabled={loading}>{loading ? <Spinner /> : "ورود"}</button>
        </form>
        <p className="text-sm mt-3">نمونه (dev seed): admin@raymand.local / ChangeMe123!</p>
      </div>
    </div>
  );
}
